import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmDiscardUnsaved } from "@/lib/unsavedGuard";

/**
 * TabbedHub — segmented underline tab strip used by all 3 hubs (My Work / My HR / Studio).
 *
 * Props:
 *   - hubKey: string used for session-storage remembering of last-used tab
 *   - tabs: array of { id, label, icon, render: () => ReactNode, hidden?: boolean }
 *
 * URL-aware: `?tab=<id>` selects the tab. On mount, falls back to last-used tab
 * for this hub (per session) or the first non-hidden tab.
 *
 * Mobile: tab strip is horizontally scrollable (no dropdown, no hamburger).
 */
export default function TabbedHub({ hubKey, tabs, paramKey = "tab", nested = false }) {
  const visible = useMemo(() => tabs.filter((t) => !t.hidden), [tabs]);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stripRef = useRef(null);

  const requested = params.get(paramKey);
  // Phase 84.44 — a deep-link / bookmark / typed URL to a tab the user no
  // longer (or never did) have access to must show "Access denied", not
  // silently fall back to whatever tab happens to be first.
  const requestedTab = requested ? tabs.find((t) => t.id === requested) : null;
  const requestedIsDenied = !!requestedTab && !!requestedTab.hidden;

  // sessionStorage stores the last tab id (e.g. "team", "finance") for this hub.
  // This is NOT sensitive — just UX state remembered across page reloads within the
  // same tab session. Safe even if XSS-exfiltrated.
  const lastUsed = (typeof window !== "undefined") ? window.sessionStorage.getItem(`hub:${hubKey}:tab`) : null;
  const fallback = visible[0]?.id;
  const activeId = requestedIsDenied ? null : (
    visible.find((t) => t.id === requested)?.id
    || visible.find((t) => t.id === lastUsed)?.id
    || fallback
  );

  // Persist last-used tab for this hub
  useEffect(() => {
    if (requestedIsDenied) return;
    if (activeId && typeof window !== "undefined") {
      window.sessionStorage.setItem(`hub:${hubKey}:tab`, activeId);
    }
  }, [hubKey, activeId, requestedIsDenied]);

  // Ensure the URL always carries the resolved tab (so deep links keep working)
  useEffect(() => {
    if (requestedIsDenied) return;
    if (!activeId) return;
    if (params.get(paramKey) !== activeId) {
      const next = new URLSearchParams(params);
      next.set(paramKey, activeId);
      setParams(next, { replace: true });
    }
  }, [activeId, requestedIsDenied]);

  // Scroll the active tab into view on mobile
  useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-tab-id="${activeId}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [activeId]);

  const current = visible.find((t) => t.id === activeId) || visible[0];

  const goto = (id) => {
    if (id !== activeId && !confirmDiscardUnsaved()) return;
    const next = new URLSearchParams(params);
    next.set(paramKey, id);
    navigate({ search: `?${next.toString()}` }, { replace: false });
  };

  return (
    <div className={nested ? "flex flex-col" : "flex flex-col min-h-[calc(100vh-3.5rem)] lg:min-h-screen"} data-testid={`hub-${hubKey}`}>
      {/* Tab strip — hidden entirely when nothing is visible (nothing to switch between) */}
      {visible.length > 0 && (
      <div className={`${nested ? "" : "sticky top-14 lg:top-0 z-20"} bg-white border-b border-slate-200`}>
        <div
          ref={stripRef}
          className="overflow-x-auto px-4 sm:px-8 max-w-7xl mx-auto"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="flex items-stretch gap-1 min-w-max">
            {visible.map((t) => {
              const isActive = t.id === activeId;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-tab-id={t.id}
                  data-testid={`hubtab-${hubKey}-${t.id}`}
                  onClick={() => goto(t.id)}
                  className={`relative inline-flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap transition-colors min-h-[44px] ${
                    isActive ? "text-[#F54800] font-semibold" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {Icon && <Icon size={15} strokeWidth={1.85} />}
                  <span>{t.label}</span>
                  {isActive && <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-[#F54800]" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Active panel */}
      <div className="flex-1 min-w-0">
        {requestedIsDenied ? (
          <AccessDeniedPanel label={requestedTab.label} />
        ) : current ? current.render?.() : (
          <AccessDeniedPanel label="any page here" empty />
        )}
      </div>
    </div>
  );
}

function AccessDeniedPanel({ label, empty }) {
  return (
    <div className="flex items-center justify-center min-h-[50vh] px-6" data-testid="tab-access-denied">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
          <span className="text-red-500 text-xl font-bold">!</span>
        </div>
        <div className="text-lg font-semibold text-slate-800 mb-1">Access denied</div>
        <p className="text-sm text-slate-500">
          {empty
            ? "You don't have access to any page here yet. Ask a Founder to grant you one from Access Management."
            : <>You don&rsquo;t have permission to view <span className="font-medium text-slate-700">{label}</span>. Ask a Founder to grant it from Access Management.</>}
        </p>
      </div>
    </div>
  );
}
